import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { commentsAPI } from '../../services/api';
import CommentForm from './CommentForm';
import CommentList from './CommentList';

const CommentSection = ({ videoId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  useEffect(() => {
    loadComments(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, sortBy]);

  const loadComments = async (reset = false) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const params = {
        sort: sortBy,
        per_page: 20
      };

      if (!reset && cursor) {
        params.cursor = cursor;
      }

      const response = await commentsAPI.getVideoComments(videoId, params);
      const newComments = response.data.comments;

      if (reset) {
        setComments(newComments);
      } else {
        setComments(prev => [...prev, ...newComments]);
      }

      setCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommentSubmit = async (content) => {
    try {
      const response = await commentsAPI.create(videoId, content);
      const newComment = response.data;

      // Add new comment to the top or appropriate position based on sort
      if (sortBy === 'newest') {
        setComments(prev => [newComment, ...prev]);
      } else if (sortBy === 'oldest') {
        setComments(prev => [...prev, newComment]);
      } else {
        // For popular, just add to top and let user re-sort if needed
        setComments(prev => [newComment, ...prev]);
      }
    } catch (error) {
      console.error('Error posting comment:', error);
      throw error;
    }
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCursor(null);
  };

  const handleCommentUpdate = (updatedComment) => {
    setComments(prev =>
      prev.map(comment => comment.id === updatedComment.id ? updatedComment : comment)
    );
  };

  const handleCommentDelete = (commentId) => {
    setComments(prev => prev.filter(comment => comment.id !== commentId));
  };

  const handleReplyAdded = (reply) => {
    // Update the parent comment's reply count
    setComments(prev =>
      prev.map(comment => {
        if (comment.id === reply.parent_id) {
          return {
            ...comment,
            replies_count: comment.replies_count + 1,
            replies: [reply, ...(comment.replies || [])]
          };
        }
        return comment;
      })
    );
  };

  return (
    <div className="mt-8">
      <h3 className="text-white font-bold text-xl uppercase tracking-tight mb-6 border-b border-gray-800 pb-4">
        Comments <span className="text-gray-500 text-base font-normal">({comments.length})</span>
      </h3>

      {/* Comment Form */}
      {user ? (
        <div className="mb-8">
          <CommentForm
            onSubmit={handleCommentSubmit}
            placeholder="Drop your take..."
            submitButtonText="Post"
          />
        </div>
      ) : (
        <div className="mb-8 p-6 bg-battle-gray rounded-xl border border-gray-800 text-center">
          <p className="text-gray-400 mb-4">Sign in to join the conversation</p>
          <button
            onClick={() => window.location.href = '/'}
            className="btn-primary"
          >
            Sign In
          </button>
        </div>
      )}

      {/* Comments List */}
      <CommentList
        comments={comments}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={() => loadComments(false)}
        onCommentUpdate={handleCommentUpdate}
        onCommentDelete={handleCommentDelete}
        onReplyAdded={handleReplyAdded}
      />
    </div>
  );
};

export default CommentSection;
